import { Typography } from '@mui/material';
import type { Currency, UsageProduct, UsageProductInput } from '@vy/types';
import { isEmptyArray } from '@vy/utils';
import React from 'react';
import { twMerge } from 'tailwind-merge';
import {
  getSectionTitle,
  getUsageProductsWithIndicesByType,
} from '../../../../../utils/usage-products-utils';
import { ChargeProvider } from '../charge-context';
import { CreatePlanSection } from '../CreatePlanSection';
import { UsageCharge } from './usage-charges-components/UsageCharge';

export type UsageChargesProps<Product extends UsageProduct | UsageProductInput> = {
  order: Set<'Tiers' | 'PerUnit' | 'Volume' | 'TieredSubscription'>;
  editable: boolean;
  sectionTitle?: string;
  usageProducts: Product[];
  titleClassName?: string;
  currency: Currency;
};

export function UsageCharges<Product extends UsageProduct | UsageProductInput>({
  order,
  editable,
  sectionTitle,
  usageProducts,
  titleClassName,
  currency,
}: UsageChargesProps<Product>) {
  const usageProductsWithIndicesByType = getUsageProductsWithIndicesByType(usageProducts);

  return (
    <CreatePlanSection
      hideCreateAnother
      title={
        editable ? undefined : (
          <p className={twMerge('text-base text-[#A6BFC7]', titleClassName)}>
            {sectionTitle ?? 'Usage Fees'}
          </p>
        )
      }
    >
      {order.size === 0 ? undefined : (
        <>
          {Array.from(order).map((title) => (
            <React.Fragment key={title}>
              {generateChargeProvidersByProduct({
                title,
                editable,
                currency,
                products: usageProductsWithIndicesByType[title],
              })}
            </React.Fragment>
          ))}
        </>
      )}
    </CreatePlanSection>
  );
}

interface ChargeProviderByProductProps {
  title: 'Tiers' | 'PerUnit' | 'Volume' | 'TieredSubscription';
  editable?: boolean;
  currency: Currency;
  showTitle?: boolean;
  products: {
    product: UsageProduct | UsageProductInput;
    productIndex: number;
  }[];
}

function generateChargeProvidersByProduct({
  title,
  editable,
  currency,
  products,
  showTitle,
}: ChargeProviderByProductProps) {
  if (isEmptyArray(products)) {
    return undefined;
  }

  return (
    <>
      {showTitle ? (
        <Typography className="text-base font-medium">{getSectionTitle(title)}</Typography>
      ) : undefined}

      {products.map(({ product, productIndex }) => (
        <ChargeProvider
          key={`${product.id}-${productIndex}`}
          value={{
            item: product,
            itemIndex: productIndex,
            editable: editable ?? false,
            currency,
          }}
        >
          <UsageCharge product={product} editable={editable ?? false} />
        </ChargeProvider>
      ))}
    </>
  );
}
